/**
 * Modern Fence Install — shared business facts (source of truth).
 *
 * PURPOSE
 * Volatile, trust-sensitive business facts were previously hardcoded in dozens
 * of components, often contradicting each other (48 states vs. 14 states vs.
 * "CT, NY & MA"; reviewCount 90 vs. 1200; "1,000s installed" with no source).
 * Anything a customer could hold us to should be read from here.
 *
 * RULES
 * 1. Only put a value here if it is verified. Unverified facts belong in
 *    UNVERIFIED_CLAIMS below and MUST NOT be rendered.
 * 2. Do not duplicate logic that already has an owner:
 *      - service-area eligibility  → src/lib/service-area.ts
 *      - pricing math              → src/lib/orderPricing.ts
 *    This module re-exports the human-readable labels only.
 * 3. See .lovable/plan/mfi-2-0-foundation-2026-09-08.md for the audit.
 */

import {
  SERVICE_AREA_LABEL,
  SERVICE_AREA_SHORT_LABEL,
  SERVICE_AREA_SHORT_FRAGMENT,
} from "@/lib/service-area";

/** Main sales line, used for tel: links and schema. */
export const PHONE_E164 = "+18884163144";
export const PHONE_DISPLAY = "(888) 416-3144";
export const PHONE_TEL_HREF = `tel:${PHONE_E164}`;

export const EMAIL = "info@modernfenceinstall.com";
export const LEGAL_NAME = "Modern Fence Install LLC";
export const BRAND_NAME = "Modern Fence Install";
export const SITE_URL = "https://modernfenceinstall.com";

/** Verified: financing is offered through Affirm. No APR claims. */
export const FINANCING_PROVIDER = "Affirm";
export const FINANCING_LABEL = "Financing available through Affirm";

/**
 * Human-readable service area. Derived from the operational config in
 * src/lib/service-area.ts so copy can never drift from eligibility logic.
 */
export const SERVICE_AREA_SENTENCE = SERVICE_AREA_LABEL;
export const SERVICE_AREA_SHORT = SERVICE_AREA_SHORT_LABEL;
/** Noun fragment — use when the sentence already supplies the verb. */
export const SERVICE_AREA_SHORT_AREAS = SERVICE_AREA_SHORT_FRAGMENT;

/**
 * States we actually operate in today, for schema `areaServed`.
 * Mirrors FULL_STATE_COVERAGE + configured states in service-area.ts.
 */
export const AREA_SERVED_SCHEMA = [
  { "@type": "State", name: "Connecticut" },
  { "@type": "State", name: "Massachusetts" },
  { "@type": "State", name: "Rhode Island" },
  { "@type": "State", name: "Vermont" },
] as const;

/**
 * Non-numeric proof wording. Safe everywhere; no counts, no ratings.
 * Use these instead of inventing volume or review numbers.
 */
export const PROOF_COPY = {
  neutralTrust: "Trusted by homeowners across our service area",
  realProjects: "Real projects. Real homeowners. Professional installation.",
  differentiators:
    "Physical barrier · No shock collar · No power · Minimal visual impact",
} as const;

/** Defensible, product-level value props (no vanity metrics). */
export const VALUE_PROOF = [
  "Physical, non-electric barrier",
  "Nearly invisible from a distance",
  "Professional installation",
  FINANCING_LABEL,
] as const;

/**
 * FACTS THAT ARE NOT VERIFIED — do not render any of these.
 * Remove an entry here (and add the real value above) only once the owner
 * confirms it in writing.
 */
export const UNVERIFIED_CLAIMS = {
  ratingValue: null,
  reviewCount: null,
  installationCount: null,
  statesServedCount: null,
  savingsPercentage: null,
  warrantyTerms: null,
  guaranteedInstallWindowDays: null,
  projectMinimum: null,
} as const;
